import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import Colors, {
  GRAY_20,
  GRAY_40,
  GRAY_60,
  GRAY150,
  LOCHMARA,
  LOCHMARA_SHADOW,
  PURE_WHITE
} from '../refs/Colors';
import { Icon } from './Icon';
import { ICON_DROPDOWN } from '../refs/Icons';
import { getColor } from '../utils/styled';

type ButtonProps = {
  className?: string;
  children?: React.ReactNode;
  icon?: string; // icon name from refs/Icons
  showDropdown?: boolean;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'text' | 'link';
  color?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isLoading?: boolean;
};

const BG_DEFAULT = Colors[LOCHMARA]; // #008DD0
const BG_HOVER = '#007ab9';
const BG_ACTIVE = Colors[LOCHMARA_SHADOW]; // #0072A9
const SECONDARY_BG_DEFAULT = Colors[GRAY_20]; // #F0F0F0
const SECONDARY_BG_HOVER = Colors[GRAY_40]; // #E5E5E5
const SECONDARY_BG_ACTIVE = Colors[GRAY_40]; // #E5E5E5


export const Content = styled.div`
  display: inline-flex;
  align-items: center;
  position: relative;
`;

export const LeftIconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  padding-left: 8px;
  padding-right: 4px;
  transition: opacity 0.3s;
  &:empty {
    padding-left: 0;
  }
`;

export const LabelWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  transition: opacity 0.3s;
`;

export const Label = styled.span<{$color: string}>`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1;
  color: ${props => getColor(props.$color)};
  text-align: center;
`;

export const RightIconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  padding-right: 2px;
  transition: opacity 0.3s;

  &:empty {
      padding-right: 0;
  }
`;

const SOLID_BASE = css`
  height: 36px;
  box-shadow: inset 0 -2px 0 rgba(0, 0, 0, 0.25);
  padding: 1px 0 4px 0; /* match figma vertical paddings */

  &:active {
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15);
  }
`;

const TEXT_BASE = css`
  height: auto;
  border: none;
  background-color: transparent;
  box-shadow: none;
  padding: 0;

  &:hover {
    opacity: 0.85;
    background-color: transparent;
    box-shadow: none;
  }

  &:active {
    opacity: 0.75;
    background-color: transparent;
    box-shadow: none;
  }
`;

const VARIANTS: Record<NonNullable<ButtonProps['variant']>, ReturnType<typeof css>> = {
  primary: css`
    ${SOLID_BASE};
    border: 1px solid ${getColor(LOCHMARA)};
    background-color: ${BG_DEFAULT};

    &:hover {
      background-color: ${BG_HOVER};
    }

    &:active {
      background-color: ${BG_ACTIVE};
    }

    .loading-spinner {
      border-color: rgba(255, 255, 255, 0.9) rgba(255, 255, 255, 0.4) rgba(255, 255, 255, 0.4);
    }
  `,
  secondary: css`
    ${SOLID_BASE};
    border: 1px solid ${getColor(GRAY_60)};
    background-color: ${SECONDARY_BG_DEFAULT};

    &:hover {
      background-color: ${SECONDARY_BG_HOVER};
    }

    &:active {
      background-color: ${SECONDARY_BG_ACTIVE};
    }

    .loading-spinner {
      border-color: rgba(90, 90, 90, 0.9) rgba(90, 90, 90, 0.35) rgba(90, 90, 90, 0.35)
    }
  `,
  text: css`
    ${TEXT_BASE}

    .loading-spinner {
      border-color: rgba(0, 141, 208, 0.9) rgba(0, 141, 208, 0.35) rgba(0, 141, 208, 0.35)
    }
  `,
  link: css`
    ${TEXT_BASE}

    ${Label} {
      text-decoration-line: underline;
      text-decoration-style: dotted;
      text-decoration-thickness: 1px;
    }

    .loading-spinner {
      border-color: rgba(0, 141, 208, 0.9) rgba(0, 141, 208, 0.35) rgba(0, 141, 208, 0.35)
    }
  `
};

const spin = keyframes`
  to {
    transform: translate(-50%, -50%) rotateZ(360deg);
  }
`;

export const Button: React.FC<ButtonProps> = styled(({
  className,
  icon,
  showDropdown,
  disabled,
  onClick,
  variant = 'primary',
  type = 'button',
  children,
  color,
  isLoading = false
}: ButtonProps) => {
  const resolvedColor =
    color ??
    (variant === 'secondary'
      ? GRAY150
      : variant === 'link'
        ? LOCHMARA_SHADOW
        : variant === 'text'
          ? LOCHMARA
          : PURE_WHITE);

  return (
    <button className={[className, isLoading ? 'is-loading' : ''].join(' ')} onClick={onClick} disabled={disabled} type={type}>
      <Content>
        <LeftIconWrap>
          {icon && <Icon color={resolvedColor} fontSize={18} size={24} icon={icon} />}
        </LeftIconWrap>
        {
          children && <LabelWrap>
            <Label $color={resolvedColor}>
              {children}
            </Label>
          </LabelWrap>
        }
      </Content>
      {showDropdown && (
        <RightIconWrap>
          <Icon color={resolvedColor} fontSize={18} size={24} icon={ICON_DROPDOWN} />
        </RightIconWrap>
      )}
      <span className="loading-spinner"></span>
    </button>
  );
})`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  position: relative;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
  min-width: auto;
  ${props => VARIANTS[props.variant ?? 'primary']}

  &:disabled,
  &[disabled] {
    opacity: 0.4;
    cursor: default;
    pointer-events: none;
  }

  .loading-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    margin: 0;
    padding: 0;
    transform: translate(-50%, -50%);
    width: 1.5em;
    height: 1.5em;
    border-radius: 50%;
    border-width: 3px;
    border-style: solid;
    opacity: 0;
    transition: opacity 0.3s;
  }

  &.is-loading {
    pointer-events: none;

    ${LabelWrap},
    ${LeftIconWrap},
    ${RightIconWrap}
    {
      opacity: 0;
    }

    .loading-spinner {
      opacity: 1;
      animation: ${spin} 0.8s linear infinite;
    }

  }
`;

export default Button;
